<script setup lang="ts">
import { ref, watch } from 'vue';
import { useNumberAnimation } from '~/composables/useNumberAnimation';
import { useShuffleAnimation } from '~/composables/useShuffleAnimation';

type Props = {
  statistics: GameStatisticsByPeriod | null;
};

const { statistics } = defineProps<Props>();

type PeriodForStatistics =
  | 'last_5_minutes'
  | 'last_1_hour'
  | 'last_24_hours'
  | 'last_7_days'
  | 'last_15_days'
  | 'last_30_days';

const { t } = useTexts();
const { showMoneyFormat } = useWallet();

const { gameStatisticsItems } = useFeatureFlagsJson();

const itemsToShow = ref(gameStatisticsItems.value);

// Define the periods to display statistics for
const displayPeriods: PeriodForStatistics[] = [
  'last_24_hours',
  'last_7_days',
  'last_15_days',
  'last_30_days',
];

// Map periods to display labels as per the user's example
const periodLabels = {
  last_24_hours: 'Hoje',
  last_7_days: '7D',
  last_15_days: '15D',
  last_30_days: '30D',
};

// Track the currently active tab (period)
const activePeriod = ref<PeriodForStatistics>('last_24_hours');

// Initialize animated values
const animatedWins = useNumberAnimation(statistics?.bets_last_24_hours || 0);
const animatedRtp = useShuffleAnimation(statistics?.rtp_last_24_hours || 0);
const animatedPlayers = useNumberAnimation(
  statistics?.players_last_24_hours || 0,
);

const animatedOnline = useShuffleAnimation(
  statistics?.players_last_1_hour || 0,
);

// Watch for activePeriod changes and update animated values
watch(activePeriod, (newPeriod) => {
  if (statistics) {
    animatedWins.updateValue(statistics[periodMapping[newPeriod]?.wins] || 0);
    animatedRtp.updateValue(statistics[periodMapping[newPeriod]?.rtp] || 0);
    animatedPlayers.updateValue(
      statistics[periodMapping[newPeriod]?.players] || 0,
    );
    // Online players always shows last_1_hour
    animatedOnline.updateValue(statistics.players_last_1_hour || 0);
  }
});

// Watch for statistics prop changes and update animated values
watch(
  () => statistics,
  (newStatistics) => {
    if (newStatistics) {
      animatedWins.updateValue(
        newStatistics[periodMapping[activePeriod.value]?.wins] || 0,
      );
      animatedRtp.updateValue(
        newStatistics[periodMapping[activePeriod.value]?.rtp] || 0,
      );
      animatedPlayers.updateValue(
        newStatistics[periodMapping[activePeriod.value]?.players] || 0,
      );
      // Online players always shows last_1_hour
      animatedOnline.updateValue(newStatistics.players_last_1_hour || 0);
    }
  },
);

const periodMapping = {
  last_5_minutes: {
    bets: 'bets_last_5_minutes',
    wins: 'wins_last_5_minutes',
    players: 'players_last_5_minutes',
    rtp: 'rtp_last_5_minutes',
    title: {
      bets: t('casino.game.statistics.last_5_minutes.bets'),
      wins: t('casino.game.statistics.last_5_minutes.wins'),
      players: t('casino.game.statistics.last_5_minutes.players'),
      rtp: t('casino.game.statistics.last_5_minutes.rtp'),
    },
  },
  last_1_hour: {
    bets: 'bets_last_1_hour',
    wins: 'wins_last_1_hour',
    players: 'players_last_1_hour',
    rtp: 'rtp_last_1_hour',
    title: {
      bets: t('casino.game.statistics.last_1_hour.bets'),
      wins: t('casino.game.statistics.last_1_hour.wins'),
      players: t('casino.game.statistics.last_1_hour.players'),
      rtp: t('casino.game.statistics.last_1_hour.rtp'),
    },
  },
  last_24_hours: {
    bets: 'bets_last_24_hours',
    wins: 'wins_last_24_hours',
    players: 'players_last_24_hours',
    rtp: 'rtp_last_24_hours',
    title: {
      bets: t('casino.game.statistics.last_24_hours.bets'),
      wins: t('casino.game.statistics.last_24_hours.wins'),
      players: t('casino.game.statistics.last_24_hours.players'),
      rtp: t('casino.game.statistics.last_24_hours.rtp'),
    },
  },
  last_7_days: {
    bets: 'bets_last_7_days',
    wins: 'wins_last_7_days',
    players: 'players_last_7_days',
    rtp: 'rtp_last_7_days',
    title: {
      bets: t('casino.game.statistics.last_7_days.bets'),
      wins: t('casino.game.statistics.last_7_days.wins'),
      players: t('casino.game.statistics.last_7_days.players'),
      rtp: t('casino.game.statistics.last_7_days.rtp'),
    },
  },
  last_15_days: {
    bets: 'bets_last_15_days',
    wins: 'wins_last_15_days',
    players: 'players_last_15_days',
    rtp: 'rtp_last_15_days',
    title: {
      bets: t('casino.game.statistics.last_15_days.bets'),
      wins: t('casino.game.statistics.last_15_days.wins'),
      players: t('casino.game.statistics.last_15_days.players'),
      rtp: t('casino.game.statistics.last_15_days.rtp'),
    },
  },
  last_30_days: {
    bets: 'bets_last_30_days',
    wins: 'wins_last_30_days',
    players: 'players_last_30_days',
    rtp: 'rtp_last_30_days',
    title: {
      bets: t('casino.game.statistics.last_30_days.bets'),
      wins: t('casino.game.statistics.last_30_days.wins'),
      players: t('casino.game.statistics.last_30_days.players'),
      rtp: t('casino.game.statistics.last_30_days.rtp'),
    },
  },
} as const;

// Get a display title for the tab based on the period
const getTabTitle = (period: PeriodForStatistics) => {
  return periodLabels[period as keyof typeof periodLabels] || '';
};
</script>

<template>
  <div
    v-if="statistics"
    class="text-white mb-4"
  >
    <div
      class="bg-[#010612] border border-solid border-white/10 rounded-[10px] p-[10px] md:p-[15px] w-full gap-[10px]"
      style="backdrop-filter: blur(20px); opacity: 1"
    >
      <div class="flex justify-between items-center">
        <span
          class="text-[15px] md:text-[20px] leading-[16px] tracking-normal font-bold"
          >Estatísticas</span
        >
        <div class="flex items-center gap-[5px]">
          <button
            v-for="period in displayPeriods"
            :key="period"
            class="text-[10px] leading-[10px] tracking-normal font-semibold p-[5px]"
            :class="{
              'rounded-[6px] p-[5px] bg-[#00C9FF1A] text-[#00C9FF]':
                activePeriod === period,
            }"
            @click="activePeriod = period"
          >
            {{ getTabTitle(period) }}
          </button>
        </div>
      </div>

      <div
        class="grid grid-cols-2 md:grid-cols-4 mt-[10px] md:mt-[20px] gap-[10px] md:gap-[15px] box-border"
      >
        <div
          v-if="itemsToShow.includes('wins')"
          class="flex items-center h-[47px] md:h-[63px] gap-[10px] rounded-[6px] p-[10px] bg-white/5 min-w-[130px]"
        >
          <div>
            <NuxtIcon
              aria-hidden="true"
              class="text-[20px] md:text-[30px]"
              filled
              name="statistics/pagou"
            />
          </div>
          <div class="flex flex-col items-start gap-[5px]">
            <span
              class="text-[#00C9FF] text-[10px] md:text-[13px] leading-[10px] md:leading-[16px] tracking-normal font-semibold"
              >Apostas</span
            >
            <span
              class="font-semibold text-[12px] leading-[12px] tracking-normal align-middle"
            >
              {{
                showMoneyFormat({
                  value: animatedWins.animatedValue.value || 0,
                })
              }}
            </span>
          </div>
        </div>

        <div
          v-if="itemsToShow.includes('rtp')"
          class="flex items-center h-[47px] md:h-[63px] min-w-[130px] gap-[10px] rounded-[6px] p-[10px] bg-white/5"
        >
          <div>
            <NuxtIcon
              aria-hidden="true"
              class="text-[20px] md:text-[30px]"
              filled
              name="statistics/rtp"
            />
          </div>
          <div class="flex flex-col items-start gap-[5px]">
            <span
              class="text-[#00C9FF] text-[10px] md:text-[13px] leading-[10px] md:leading-[16px] tracking-normal font-semibold"
              >RTP</span
            >
            <span
              class="font-semibold text-[12px] leading-[12px] tracking-normal align-middle"
            >
              {{ `${animatedRtp.animatedValue.value || 0}%` }}
            </span>
          </div>
        </div>

        <div
          v-if="itemsToShow.includes('players')"
          class="flex items-center h-[47px] md:h-[63px] min-w-[130px] gap-[10px] rounded-[6px] p-[10px] bg-white/5"
        >
          <div>
            <NuxtIcon
              aria-hidden="true"
              class="text-[20px] md:text-[30px]"
              filled
              name="statistics/jogadores"
            />
          </div>
          <div class="flex flex-col items-start gap-[5px]">
            <span
              class="text-[#00C9FF] text-[10px] md:text-[13px] leading-[10px] md:leading-[16px] tracking-normal font-semibold"
              >Jogadores</span
            >
            <span
              class="font-semibold text-[12px] leading-[12px] tracking-normal align-middle"
            >
              {{ animatedPlayers.animatedValue.value || 0 }}
            </span>
          </div>
        </div>

        <div
          class="flex items-center h-[47px] md:h-[63px] min-w-[130px] gap-[10px] rounded-[6px] p-[10px] bg-white/5"
        >
          <div>
            <NuxtIcon
              aria-hidden="true"
              class="text-[20px] md:text-[30px]"
              filled
              name="statistics/online"
            />
          </div>
          <div class="flex flex-col items-start gap-[5px]">
            <span
              class="text-[#00C9FF] text-[10px] md:text-[13px] leading-[10px] md:leading-[16px] tracking-normal font-semibold"
              >Online agora</span
            >
            <span
              class="font-semibold text-[12px] leading-[12px] tracking-normal align-middle flex gap-[10px]"
            >
              <span
                class="bg-[#00ff00] rounded-full text-[color:var(--color-light)] w-[7px] h-[7px] md:w-[9px] md:h-[9px] self-center mb-0.5 animate-[blink_1800ms_linear_infinite]"
              >
              </span>
              {{ animatedOnline.animatedValue.value || 0 }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
